import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { Card } from "../../Components/Card";
import { useRef, useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import primeClient from "../../axios/primeClient";
import { parseDateString, timeAgo } from "../../utils/timeAgo";
import hiClient from "../../axios/hiClient";
import { Modal } from "../../Components/Modal";
import { PrimaryButton, SecondaryButton } from "../../Components/Button";
import { FunnelIcon } from "@heroicons/react/20/solid";
import { DateRangeInput } from "../../Components/DateRangeInput";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Colors } from "../../constants/Colors";

// prettier-ignore
const alarmStatusColor = {
  "unread": Colors.alertsred100,
  "read": Colors.alertsyellow100,
};

// prettier-ignore
const alarmTypeColor = {
  "attention": Colors.alertsyellow100,
  "down": Colors.alertsred100,

  "online": Colors.alertsgreen100,
  "offline": Colors.alertsred100,
  "technical-issue": "#a7a9ac",
  "unknown": "#a7a9ac",
  "suspend": Colors.alertsyellow100,
}

export const AlarmsViewAll = ({ alarmStatus }) => {
  const loginType = localStorage.getItem("login_type");
  return (
    <div
      style={{
        padding: "2rem 1rem 0 1rem",
        width: "100%",
        height: "calc(100vh - 62px)",
        overflow: "hidden",
      }}
    >
      <Title alarmStatus={alarmStatus} />
      <Description alarmStatus={alarmStatus} />
      {loginType === "prime" ? (
        <PrimeAlarmDetail alarmStatus={alarmStatus} />
      ) : (
        <HiAlarmDetail alarmStatus={alarmStatus} />
      )}
    </div>
  );
};

const Title = ({ alarmStatus }) => {
  const title = alarmStatus[0].toUpperCase() + alarmStatus.slice(1);

  return (
    <div
      style={{
        fontWeight: "bold",
        fontSize: "1.25rem",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: "1rem",
          height: "1rem",
          borderRadius: "50%",
          backgroundColor: alarmStatusColor[alarmStatus],
          marginRight: "1rem",
        }}
      ></span>
      {title}
    </div>
  );
};

const Description = ({ alarmStatus }) => {
  const { t } = useTranslation();
  // prettier-ignore
  const alarmStatusDescription = {
    "unread": t("prime.alarm.unreadDesc"),
    "read": t("prime.alarm.readDesc"),
  };
  return (
    <Card
      style={{
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
      }}
    >
      {alarmStatusDescription[alarmStatus]}
    </Card>
  );
};

const PrimeAlarmDetail = ({ alarmStatus }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const defaultFilter = {
    alertAttention: true,
    alertDown: true,
    timeRange: "All",
    startDate: "",
    endDate: "",
  };
  const [filterState, setFilterState] = useState({ ...defaultFilter });
  const inputRef = useRef();

  const handleSearch = () => {
    setSearchQuery(inputRef.current.value);
  };

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const onFilterApply = (filter) => {
    setFilterState(filter);
  };

  const onFilterReset = () => {
    setFilterState({ ...defaultFilter });
  };

  const { data: alarmStatusResponse } = useFetch({
    url: `/v1/apps/alarm`,
    client: primeClient,
    initialData: { alarms: [] },
  });

  const dataMapper = (el) => ({
    id: el.id,
    name: el.data.name,
    identifier: el.data.ip_address,
    lastUpdated: timeAgo(el.data.last_update, t),
    description: [],
    alarmType: el.alarm_type,
    lastUpdatedTime: parseDateString(el.data.last_update),
  });

  const filterByAlarmStatus = (el) =>
    alarmStatus === "read" ? el.is_read : !el.is_read;

  const filterBySearchQuery = (el) =>
    searchQuery.trim() === "" ||
    el.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    el.identifier?.toLowerCase().includes(searchQuery.toLowerCase());

  const filterByAlert = (el) => {
    if (filterState.alertAttention && el.alarmType === "attention") {
      return true;
    }
    if (filterState.alertDown && el.alarmType === "down") {
      return true;
    }
    return false;
  };

  const filterByTime = (el) => {
    if (filterState.timeRange === "All") {
      return true;
    }

    const date = el.lastUpdatedTime;
    if (!date) {
      return false;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (filterState.timeRange === "Today" && date >= today) {
      return true;
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (
      filterState.timeRange === "Yesterday" &&
      date >= yesterday &&
      date < today
    ) {
      return true;
    }

    const last7Days = new Date(today);
    last7Days.setDate(today.getDate() - 7);
    if (
      filterState.timeRange === "Last 7 Days" &&
      date >= last7Days &&
      date < today
    ) {
      return true;
    }

    const startDate = new Date(filterState.startDate);
    const endDate = new Date(filterState.endDate);

    if (filterState.startDate && filterState.endDate) {
      return date >= startDate && date <= endDate;
    }
    if (filterState.startDate) {
      return date >= startDate;
    }
    if (filterState.endDate) {
      return date <= endDate;
    }
    return false;
  };

  return (
    <>
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <PrimeFilterModalContent
          initialState={filterState}
          onApply={onFilterApply}
          onReset={onFilterReset}
          toggle={toggleModal}
        />
      </Modal>
      <Card
        style={{
          marginTop: "0.5rem",
          marginBottom: "0.5rem",
          padding: "10px",
          borderRadius: "10px",
          backgroundColor: "#ffffff",
          height: "80%",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1rem",
          }}
        >
          <div
            style={{
              flexGrow: "1",
              display: "flex",
              alignItems: "center",
              padding: "10px",
              borderRadius: "10px",
              color: "#6a6a6a",
              display: "flex",
              alignItems: "center",
              color: "#6a6a6a",
              borderBlockStyle: "solid",
              borderWidth: "1px",
              borderColor: "#6a6a6a",
            }}
          >
            <MagnifyingGlassIcon
              width={"1.4rem"}
              height={"1.4rem"}
              style={{ cursor: "pointer", marginRight: "10px" }}
              onClick={handleSearch}
            />
            <input
              type="text"
              placeholder={t("hi.deviceCondition.search.placeholder")}
              ref={inputRef}
              style={{
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                color: "#6a6a6a",
                flex: "1",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </div>
          <SecondaryButton
            onClick={toggleModal}
            icon={<FunnelIcon width={"1rem"} />}
            style={{ padding: "0.5rem" }}
          >
            Filter
          </SecondaryButton>
        </div>

        <div
          style={{
            gap: "5px",
            marginTop: "1rem",
            overflowX: "hidden",
            overflowY: "scroll",
            flexGrow: "1",
            height: "80%",
          }}
        >
          {alarmStatusResponse.alarms
            .filter(filterByAlarmStatus)
            .map(dataMapper)
            .filter((el) => {
              return (
                filterBySearchQuery(el) && filterByAlert(el) && filterByTime(el)
              );
            })
            .sort((a, b) => b.lastUpdatedTime - a.lastUpdatedTime)
            .map((el, idx) => (
              <CardSubItem
                alarmStatus={alarmStatus}
                subItem={el}
                key={idx}
                alarmType={el.alarmType}
                id={el.id}
              />
            ))}
        </div>
      </Card>
    </>
  );
};

const PrimeFilterModalContent = ({
  initialState,
  onApply,
  onReset,
  toggle,
}) => {
  const [alertAttention, setAlertAttention] = useState(
    initialState.alertAttention
  );
  const { t } = useTranslation();
  const [alertDown, setAlertDown] = useState(initialState.alertDown);
  const [timeRange, setTimeRange] = useState(initialState.timeRange);
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);

  return (
    <div style={{ width: "21rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <h3
          style={{
            fontWeight: "bold",
            fontSize: "1.25rem",
            margin: "0.5rem 0",
          }}
        >
          {t("prime.alarm.byAlert")}
        </h3>

        {/* Attention Checkbox */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.5rem",
          }}
        >
          <input
            type="checkbox"
            id="attention"
            name="attention"
            checked={alertAttention}
            onChange={(e) => setAlertAttention(e.target.checked)}
            style={{ width: "24px", height: "24px" }}
          />
          <label htmlFor="attention">Attention</label>
        </div>

        {/* Down Checkbox */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.5rem",
          }}
        >
          <input
            type="checkbox"
            id="down"
            name="down"
            checked={alertDown}
            onChange={(e) => setAlertDown(e.target.checked)}
            style={{ width: "24px", height: "24px" }}
          />
          <label htmlFor="down">Down</label>
        </div>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <h3
          style={{
            fontWeight: "bold",
            fontSize: "1.25rem",
            margin: "0.5rem 0",
          }}
        >
          {t("prime.alarm.byTime")}
        </h3>

        {/* Time Range Selector */}
        <select
          id="time"
          name="time"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "10px",
            border: "1px solid rgb(84, 93, 101)",
            width: "100%",
          }}
        >
          <option value="All">{t("prime.alarm.all")}</option>
          <option value="Today">{t("prime.alarm.today")}</option>
          <option value="Yesterday">{t("prime.alarm.yesterday")}</option>
          <option value="Last 7 Days">{t("prime.alarm.last7days")}</option>
          <option value="Select Date">{t("prime.alarm.selectDate")}</option>
        </select>

        {/* Date Range Input */}
        {timeRange === "Select Date" && (
          <DateRangeInput
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
        <SecondaryButton
          style={{ flexGrow: "1" }}
          onClick={() => {
            onReset();
            toggle();
          }}
        >
          {t("prime.alarm.reset")}
        </SecondaryButton>
        <PrimaryButton
          style={{ flexGrow: "1" }}
          onClick={() => {
            onApply({
              alertAttention,
              alertDown,
              timeRange,
              startDate,
              endDate,
            });
            toggle();
          }}
        >
          {t("prime.alarm.apply")}
        </PrimaryButton>
      </div>
    </div>
  );
};

const HiAlarmDetail = ({ alarmStatus }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef();
  const { t } = useTranslation();

  const handleSearch = () => {
    setSearchQuery(inputRef.current.value);
  };

  const { data: alarmStatusResponse } = useFetch({
    url: "/alarm/list",
    client: hiClient,
    initialData: {
      count_total: 0,
      count_acknowledge: 0,
      count_unacknowledge: 0,
      count_offline: 0,
      count_tech_issue: 0,
      count_suspend: 0,
      alarms: [],
    },
  });

  const dataMapper = (el) => ({
    name: el.name,
    identifier: el.nd,
    lastUpdated: el.updated_at ? timeAgo(el.updated_at, t) : "unknown",
    description: [`Device ${el.device_status}`],
    alarmType: el.device_status,
    lastUpdatedTime: parseDateString(el.updated_at),
  });

  return (
    <Card
      style={{
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        height: "80%",
      }}
    >
      <div
        style={{
          flexGrow: "1",
          display: "flex",
          alignItems: "center",
          padding: "10px",
          borderRadius: "10px",
          color: "#6a6a6a",
          display: "flex",
          alignItems: "center",
          color: "#6a6a6a",
          borderBlockStyle: "solid",
          borderWidth: "1px",
          borderColor: "#6a6a6a",
        }}
      >
        <MagnifyingGlassIcon
          width={"1.4rem"}
          height={"1.4rem"}
          style={{ cursor: "pointer", marginRight: "10px" }}
          onClick={handleSearch}
        />
        <input
          type="text"
          placeholder={t("hi.deviceCondition.search.placeholder")}
          ref={inputRef}
          style={{
            backgroundColor: "transparent",
            border: "none",
            outline: "none",
            color: "#6a6a6a",
            flex: "1",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
      </div>
      <div
        style={{
          gap: "5px",
          marginTop: "1rem",
          overflowX: "hidden",
          overflowY: "scroll",
          flexGrow: "1",
          height: "80%",
        }}
      >
        {alarmStatusResponse.alarms
          .map(dataMapper)
          .sort((a, b) => b.lastUpdatedTime - a.lastUpdatedTime)
          .filter(
            (el) =>
              searchQuery.trim() === "" ||
              el.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              el.identifier?.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((el, idx) => (
            <CardSubItem
              alarmStatus={alarmStatus}
              subItem={el}
              key={idx}
              alarmType={el.alarmType}
              id={el.id}
            />
          ))}
      </div>
    </Card>
  );
};

const CardSubItem = ({
  alarmStatus,
  subItem: { name, identifier, lastUpdated, description, id },
  alarmType,
}) => {
  const navigate = useNavigate();
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        margin: "8px",
        marginBottom: "0",
        flexGrow: "1",
        cursor: "pointer",
        borderLeft: `5px solid ${alarmTypeColor[alarmType]}`,
        paddingLeft: "0.4rem",
      }}
      onClick={() => {
        navigate(`/alarms/${id}`);
      }}
    >
      <div
        style={{
          flexGrow: "1",
          fontSize: "0.8rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "95%",
            fontWeight: "bold",
          }}
        >
          <div>{name}</div>
          <div style={{ fontWeight: 600 }}>{lastUpdated}</div>
        </div>
        <div style={{ color: "#525252" }}>{identifier}</div>
        <div>
          {description.map((el) => (
            <>
              {el}
              <br />
            </>
          ))}
        </div>
      </div>
    </div>
  );
};
